import { callOrReturn, getExtensionField } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Selection, TextSelection } from '@tiptap/pm/state';
import type { TableRect } from '@tiptap/pm/tables';
import {
  addColumn,
  addColumnAfter,
  addColumnBefore,
  addRow,
  addRowAfter,
  addRowBefore,
  cellAround,
  CellSelection,
  deleteColumn,
  deleteRow,
  deleteTable,
  fixTables,
  goToNextCell,
  mergeCells,
  selectedRect,
  splitCell,
  toggleHeader,
} from '@tiptap/pm/tables';
import {
  addColumnAt,
  addRowAt,
  findTable,
  removeColumnAt,
  removeRowAt,
  selectColumn,
  selectRow,
  selectTable,
} from 'prosemirror-utils';

import { TIPTAP_TABLE_TYPE } from './../../ricos-content';
import type { ExtensionProps, NodeConfig, RicosExtension } from './../../ricos-types';
import { createRicosNodeExtension } from './../../plugin-commons';
import type { TiptapAdapterServices } from './../../tiptap-editor/initializeTiptapAdapter';

import { TRANSACTION_META_KEYS } from './consts';
import {
  addRowColPlugin,
  columnResizingPlugin,
  handlersPlugin,
  rowResizingPlugin,
  tableEditingPlugin,
  TableView,
} from './plugins';
import { TableQuery } from './TableQuery';
import { createTable } from './utilities/createTable';
import { isCellSelection } from './utilities/is-selection-type';
import { keyboardShortcuts, maybeToggleEditMode } from './utilities/keyboardShortcuts';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    table: {
      /**
       * delete table in selection
       */
      deleteTable: () => ReturnType;
      setCellBorderColor: (color: string) => ReturnType;
      setOutsiderCellsBorderColor: (color: string) => ReturnType;
      setCellAttribute: (name: string, value: unknown) => ReturnType;
      updateCellAttribute: (name: string, value: unknown) => ReturnType;
      toggleTableAttribute: (name: string) => ReturnType;
      addColumnBefore: () => ReturnType;
      addColumnAfter: () => ReturnType;
      addColumn: (rect: TableRect, index: number) => ReturnType;
      deleteColumn: () => ReturnType;
      addRowBefore: () => ReturnType;
      addRowAfter: () => ReturnType;
      addRow: (rect: TableRect, index: number) => ReturnType;
      deleteRow: () => ReturnType;
      mergeCells: () => ReturnType;
      splitCell: () => ReturnType;
      toggleHeaderColumn: () => ReturnType;
      toggleHeaderRow: () => ReturnType;
      mergeOrSplit: () => ReturnType;
      goToNextCell: () => ReturnType;
      goToPreviousCell: () => ReturnType;
      fixTables: () => ReturnType;
      setCellSelection: (pos: number) => ReturnType;
      selectColumnAtIndex: (index: number) => ReturnType;
      selectColumn: () => ReturnType;
      deleteColumnAtIndex: (index: number) => ReturnType;
      addColumnAtIndex: (index: number) => ReturnType;
      selectRowAtIndex: (index: number) => ReturnType;
      selectRow: () => ReturnType;
      deleteRowAtIndex: (index: number) => ReturnType;
      addRowAtIndex: (index: number) => ReturnType;
      selectWholeTable: () => ReturnType;
      setTableRowHeight: (height: number, pos: number, node: ProseMirrorNode) => ReturnType;
      distributeRows: () => ReturnType;
      distributeColumns: () => ReturnType;
      setEditCell: () => ReturnType;
      clearCell: () => ReturnType;
      reorderRows: (from: { start: number; end: number }, to: number) => ReturnType;
      reorderColumns: (from: { start: number; end: number }, to: number) => ReturnType;
    };
  }
}

export const tableExtension = createRicosNodeExtension({
  groups: ['shortcuts-enabled', 'footer-disabled'],
  type: 'html-node',
  reconfigure: (
    config: NodeConfig,
    _extensions: RicosExtension[],
    _props: ExtensionProps,
    settings: Record<string, unknown>,
    services: TiptapAdapterServices
  ) => ({
    ...config,
    addOptions: () => ({
      services,
      ...settings,
      View: TableView,
    }),
  }),
  name: TIPTAP_TABLE_TYPE,
  createExtensionConfig({ Plugin, PluginKey }) {
    return {
      name: this.name,
      group: 'block collapsibleListItemBodyContent',
      selectable: true,
      isolating: true,
      draggable: true,
      content: 'TABLE_ROW+',
      tableRole: 'table',
      priority: 101,
      contentDOM: document.createElement('tbody'),
      addAttributes: () => ({
        containerData: null,
        dimensions: null,
        header: null,
        rowHeader: null,
        columnHeader: null,
      }),
      parseHTML() {
        return [{ tag: 'table' }];
      },

      renderHTML({ HTMLAttributes }) {
        return ['table', HTMLAttributes, ['tbody', 0]];
      },

      addProseMirrorPlugins() {
        return [
          handlersPlugin(Plugin, PluginKey, this.editor),
          addRowColPlugin(Plugin, PluginKey, this.editor, this.options.services),
          columnResizingPlugin(Plugin, PluginKey, this.editor),
          rowResizingPlugin(Plugin, PluginKey, this.editor),
          tableEditingPlugin(Plugin, PluginKey, this.editor),
        ];
      },

      extendNodeSchema(extension) {
        const context = {
          name: extension.name,
          options: extension.options,
          storage: extension.storage,
        };

        return {
          tableRole: callOrReturn(getExtensionField(extension, 'tableRole', context)),
        };
      },

      addNodeView() {
        return ({ node, editor }) => new TableView(node, editor);
      },

      addCommands() {
        return {
          insertTable:
            ({ rows = 3, cols = 3, withHeaderRow = true } = {}) =>
            ({ tr, dispatch, editor }: $TSFixMe) => {
              const node = createTable(editor.schema, rows, cols, withHeaderRow);

              if (dispatch) {
                const offset = tr.selection.anchor + 1;

                tr.replaceSelectionWith(node)
                  .scrollIntoView()
                  .setSelection(TextSelection.near(tr.doc.resolve(offset)));
              }

              return true;
            },
          addColumnBefore:
            () =>
            ({ state, chain }) => {
              const table = TableQuery.of(state.selection);
              const selectedCol = table.getColsIndexesInSelection();
              return chain()
                .command(({ state, dispatch }) => addColumnBefore(state, dispatch))
                .command(({ commands }) => {
                  if (selectedCol.length > 0) {
                    commands.selectColumnAtIndex(selectedCol[0]);
                    return true;
                  }

                  return false;
                })
                .run();
            },
          addColumnAfter:
            () =>
            ({ state, chain }) => {
              const table = TableQuery.of(state.selection);
              const selectedCol = table.getColsIndexesInSelection();
              return chain()
                .command(({ state, dispatch }) => addColumnAfter(state, dispatch))
                .command(({ commands }) => {
                  const length = selectedCol.length;
                  if (length > 0) {
                    commands.selectColumnAtIndex(selectedCol[length - 1] + 1);
                    return true;
                  }
                  return false;
                })
                .run();
            },
          addColumn:
            (rect, index) =>
            ({ dispatch, tr }) => {
              return dispatch?.(addColumn(tr, rect, index));
            },
          deleteColumn:
            () =>
            ({ state, dispatch }) => {
              return deleteColumn(state, dispatch);
            },
          addRowBefore:
            () =>
            ({ state, chain }) => {
              const table = TableQuery.of(state.selection);
              const selectedRow = table.getRowsIndexesInSelection();
              return chain()
                .command(({ state, dispatch }) => addRowBefore(state, dispatch))
                .command(({ commands }) => {
                  if (selectedRow.length > 0) {
                    commands.selectRowAtIndex(selectedRow[0]);
                    return true;
                  }
                  return false;
                })
                .run();
            },
          addRowAfter:
            () =>
            ({ state, chain }) => {
              const table = TableQuery.of(state.selection);
              const selectedRow = table.getRowsIndexesInSelection();
              return chain()
                .command(({ state, dispatch }) => addRowAfter(state, dispatch))
                .command(({ commands }) => {
                  const length = selectedRow.length;
                  if (length > 0) {
                    commands.selectRowAtIndex(selectedRow[length - 1] + 1);
                    return true;
                  }
                  return false;
                })
                .run();
            },
          addRow:
            (rect, index) =>
            ({ dispatch, tr }) => {
              return dispatch?.(addRow(tr, rect, index));
            },
          deleteRow:
            () =>
            ({ state, dispatch }) => {
              return deleteRow(state, dispatch);
            },
          deleteTable:
            () =>
            ({ state, dispatch }) => {
              return deleteTable(state, dispatch);
            },
          mergeCells:
            () =>
            ({ state, dispatch }) => {
              return mergeCells(state, dispatch);
            },
          splitCell:
            () =>
            ({ state, dispatch }) => {
              return splitCell(state, dispatch);
            },
          toggleHeaderColumn:
            () =>
            ({ chain }) => {
              return chain()
                .toggleTableAttribute('columnHeader')
                .command(({ state, dispatch }) => {
                  toggleHeader('column')(state, dispatch);
                  return true;
                })
                .run();
            },
          toggleHeaderRow:
            () =>
            ({ chain }) => {
              return chain()
                .toggleTableAttribute('rowHeader')
                .command(({ state, dispatch }) => {
                  toggleHeader('row')(state, dispatch);
                  return true;
                })
                .run();
            },
          mergeOrSplit:
            () =>
            ({ state, dispatch }) => {
              if (mergeCells(state, dispatch)) {
                return true;
              }

              return splitCell(state, dispatch);
            },
          setCellAttribute:
            (name, value) =>
            ({ state, dispatch, tr }) => {
              if (!isCellSelection(state.selection)) {
                return false;
              }
              state.selection.forEachCell((node, pos) => {
                if (node.attrs[name] !== value)
                  tr.setNodeMarkup(pos, null, { ...node.attrs, [name]: value });
              });
              dispatch?.(tr);

              return true;
            },
          updateCellAttribute:
            (name, value) =>
            ({ state, dispatch, tr }) => {
              if (!isCellSelection(state.selection)) {
                return false;
              }
              state.selection.forEachCell((node, pos) => {
                if (node.attrs[name] !== value)
                  tr.setNodeMarkup(pos, null, {
                    ...node.attrs,
                    [name]: { ...(node.attrs[name] || {}), ...(value ?? {}) },
                  });
              });
              dispatch?.(tr);

              return true;
            },
          toggleTableAttribute:
            name =>
            ({ state, dispatch }) => {
              const parentTable = findTable(state.selection);
              if (parentTable) {
                const { tr } = state;
                const table = parentTable.node;
                tr.setNodeMarkup(parentTable.pos, undefined, {
                  ...table.attrs,
                  [name]: !table.attrs[name],
                });
                dispatch?.(tr);
              }
              return true;
            },
          goToNextCell:
            () =>
            ({ state, dispatch }) => {
              return goToNextCell(1)(state, dispatch);
            },
          goToPreviousCell:
            () =>
            ({ state, dispatch }) => {
              return goToNextCell(-1)(state, dispatch);
            },
          fixTables:
            () =>
            ({ state, dispatch }) => {
              if (dispatch) {
                fixTables(state);
              }

              return true;
            },
          setCellSelection:
            pos =>
            ({ tr, dispatch }) => {
              if (dispatch) {
                const selection = CellSelection.create(tr.doc, pos);
                tr.setMeta(TRANSACTION_META_KEYS.SELECTED_CELL, { pos });
                tr.setSelection(selection);
              }
              return true;
            },
          selectColumnAtIndex:
            index =>
            ({ tr, dispatch }) => {
              tr.setMeta(TRANSACTION_META_KEYS.SELECT_COLUMN, index);
              dispatch?.(selectColumn(index)(tr));
              return true;
            },
          selectColumn:
            () =>
            ({ state, commands }) => {
              const rect = selectedRect(state);
              return commands.selectColumnAtIndex(rect.left);
            },
          deleteColumnAtIndex:
            index =>
            ({ tr, dispatch }) => {
              dispatch?.(removeColumnAt(index)(tr));
              return true;
            },
          addColumnAtIndex:
            index =>
            ({ tr, dispatch }) => {
              dispatch?.(addColumnAt(index)(tr));
              return true;
            },
          selectRowAtIndex:
            index =>
            ({ tr, dispatch }) => {
              tr.setMeta(TRANSACTION_META_KEYS.SELECT_ROW, index);
              dispatch?.(selectRow(index)(tr));
              return true;
            },
          selectRow:
            () =>
            ({ state, commands }) => {
              const rect = selectedRect(state);
              return commands.selectRowAtIndex(rect.top);
            },
          deleteRowAtIndex:
            index =>
            ({ tr, dispatch }) => {
              dispatch?.(removeRowAt(index)(tr));
              return true;
            },
          addRowAtIndex:
            index =>
            ({ tr, dispatch }) => {
              dispatch?.(addRowAt(index)(tr));
              return true;
            },
          selectWholeTable:
            () =>
            ({ tr, dispatch }) => {
              dispatch?.(selectTable(tr));
              return true;
            },
          setTableRowHeight:
            (height, pos, node) =>
            ({ tr, dispatch }) => {
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, height });
              dispatch?.(tr);
              return true;
            },
          distributeRows:
            () =>
            ({ tr, dispatch, state }) => {
              const table = TableQuery.of(state.selection);
              if (table) {
                const rows = table.getRowsIndexesInSelection();
                const tableAttrs = table.getNode().attrs;
                const totalHeight = [...tableAttrs.dimensions.rowsHeight]
                  .splice(rows[0], rows[rows.length - 1])
                  .reduce((curr, acc) => curr + acc, 0);
                const newHeight = totalHeight / rows.length;
                table.getNode().content.forEach((node, pos, index) => {
                  rows.includes(index) &&
                    tr.setNodeMarkup(pos + table.getStartPos(), undefined, {
                      ...node.attrs,
                      height: newHeight,
                    });
                });
                dispatch?.(tr);
                return true;
              }
              return false;
            },
          distributeColumns:
            () =>
            ({ tr, dispatch, state, view }) => {
              const table = TableQuery.of(state.selection);
              if (table) {
                const cols = table.getColsIndexesInSelection();
                const totalWidth = table.getDomWidthInSelection(view);
                const rowsIndexes = Array(table.getHeight()).fill(0);
                const map = table.getMap();
                rowsIndexes.forEach((_, row) =>
                  cols.forEach(col => {
                    const mapIndex = row * table.getWidth() + col;
                    tr.setNodeMarkup(table.getStartPos() + map[mapIndex], null, {
                      colwidth: [totalWidth / cols.length],
                    });
                  })
                );

                dispatch?.(tr);
                return true;
              }
              return false;
            },
          setEditCell:
            () =>
            ({ tr, dispatch, state }) => {
              const $anchor = cellAround(state.selection.$anchor);
              if ($anchor) {
                tr.setMeta(TRANSACTION_META_KEYS.EDITING_CELL, { pos: $anchor });
                const selection = Selection.near(state.selection.$head, -1);
                tr.setSelection(selection);
                dispatch?.(tr);
                return true;
              }
              return false;
            },
          clearCell:
            () =>
            ({ tr, dispatch }) => {
              dispatch?.(tr.deleteSelection());
              return true;
            },
          setCellBorderColor:
            color =>
            ({ chain }) => {
              return chain()
                .focus()
                .setCellAttribute('borderColors', {
                  top: color,
                  right: color,
                  bottom: color,
                  left: color,
                })
                .run();
            },
          setOutsiderCellsBorderColor:
            color =>
            ({ state, dispatch, tr }) => {
              const table = TableQuery.of(state.selection);
              if (table) {
                const start = table.getStartPos();
                if (!isCellSelection(state.selection)) {
                  return false;
                }
                state.selection.forEachCell((node, pos) => {
                  const cellNeighbors = table.getCellNeighbors(pos);
                  const isNeighborInSelection = (neighbor: $TSFixMe) =>
                    cellNeighbors[neighbor as $TSFixMeKey<typeof cellNeighbors>] &&
                    state.selection.ranges.find(
                      sel =>
                        sel.$from.pos - start - 1 ===
                        cellNeighbors[neighbor as $TSFixMeKey<typeof cellNeighbors>]
                    );

                  const borders: {
                    top?: string;
                    bottom?: string;
                    left?: string;
                    right?: string;
                  } = {};
                  !isNeighborInSelection('top') && (borders.top = color);
                  !isNeighborInSelection('bottom') && (borders.bottom = color);
                  !isNeighborInSelection('left') && (borders.left = color);
                  !isNeighborInSelection('right') && (borders.right = color);

                  tr.setNodeMarkup(pos, null, {
                    ...node.attrs,
                    borderColors: { ...node.attrs.borderColors, ...borders },
                  });
                });
                dispatch?.(tr);
                return true;
              }

              return false;
            },
          reorderRows:
            (from, to) =>
            ({ state, chain }) => {
              const table = TableQuery.of(state.selection);
              const tableStart = table.getStartPos();
              const tableNode = table.getNode();
              const draggedRows: ProseMirrorNode[] = [];
              tableNode.forEach((row, pos, index) => {
                index >= from.start && index <= from.end && draggedRows.push(row);
              });

              let rowPos = tableStart;
              for (let i = 0; i < to; i++) rowPos += tableNode.child(i).nodeSize;

              return chain()
                .command(({ tr, dispatch }) => dispatch?.(tr.insert(rowPos, draggedRows)))
                .command(({ dispatch, commands }) => {
                  dispatch?.(commands.deleteRow());
                  return true;
                })
                .run();
            },
          reorderColumns:
            (from, to) =>
            ({ state, chain, tr }) => {
              const table = TableQuery.of(state.selection);
              const tableNode = table.getNode();

              tableNode.forEach((row, pos, rowIndex) => {
                const draggedCells: ProseMirrorNode[] = [];
                row.forEach((col, pos, index) => {
                  index >= from.start && index <= from.end && draggedCells.push(col);
                });
                const dropPos = table.map.positionAt(rowIndex, to, table.getNode());
                tr.insert(tr.mapping.map(table.getStartPos() + dropPos), draggedCells);
              });

              return chain()
                .command(({ tr, dispatch }) => dispatch?.(tr))
                .command(({ dispatch, commands }) => {
                  dispatch?.(commands.deleteColumn());
                  return true;
                })
                .run();
            },
        };
      },

      addKeyboardShortcuts() {
        const newLine = maybeToggleEditMode;
        const tiptapKeyboardShortcuts = keyboardShortcuts;

        const newLineShortcuts: any = [];
        this.options.services.shortcuts.shortcuts.forEach((shortcut: $TSFixMe) => {
          if (shortcut.shortcut.name === 'NewLine') {
            newLineShortcuts.push(shortcut);
          }
        });

        newLineShortcuts.forEach((newLineShortcut: $TSFixMe) => {
          const modifier = newLineShortcut.keys.macOs.modifiers[0];
          const key = newLineShortcut.keys.macOs.key;
          const shortcutKey = modifier ? `${modifier}-${key}` : key;
          tiptapKeyboardShortcuts[shortcutKey as $TSFixMeKey<typeof tiptapKeyboardShortcuts>] =
            newLine;
        });

        return tiptapKeyboardShortcuts;
      },
    };
  },
});
